import React from 'react';
import { useTranslation } from 'react-i18next';

import './index.less';

import { AgentMessageEventOutput, AgentMessageEventType, AgentMessageModel } from '../../../../gamma-api';
import IbTypography from '../../common/IbTypography';
import ToolCallEvent, { ToolCallEventSize } from '../ToolCallEvent';
import IbCollapse, { IbCollapsePanel } from '../../common/IbCollapse';
import IbIcon from '../../common/IbIcon';
import RuntimeErrorEvent, { RuntimeErrorEventSize } from '../RuntimeErrorEvent';
import HandoffEvent, { HandoffEventSize } from '../HandoffEvent';
import GuardrailChecksEvent, { GuardrailChecksEventSize } from '../GuardrailChecksEvent';
import { GuardrailEventData, isGuardrailEvent } from '../guardrailChecks';
import ChatCompressionEvent, { ChatCompressionEventSize } from '../ChatCompressionEvent';
import MarkdownContent from '../../MarkdownContent';

import { printAgentReflections } from './utils';

const MAIN_CLASS_NAME = 'message-events';
const ASSISTANT_MESSAGE_CLASS_NAME = `${MAIN_CLASS_NAME}__assistant-message`;
const REFLECTIONS_CLASS_NAME = `${MAIN_CLASS_NAME}__reflections`;

export interface IMessageEventsProps {
  message: AgentMessageModel;
  selectedMessageId?: string;
  selectedMessageEvent?: AgentMessageEventOutput;
  onEventSelect: (eventId: string) => void;
}

const MessageEvents: React.FC<IMessageEventsProps> = ({
  message,
  selectedMessageId,
  selectedMessageEvent,
  onEventSelect,
}) => {
  const { t } = useTranslation();

  const hasVisibleEvents = message.events.some((event) => event.data.event_type !== AgentMessageEventType.LlmCall);
  if (!hasVisibleEvents) {
    return null;
  }

  const isCurrentMessageSelected = selectedMessageId === message.message_id;
  const guardrailEvents = message.events.filter(isGuardrailEvent);
  const firstGuardrailEvent = guardrailEvents[0];
  const guardrailData: GuardrailEventData = {
    ...(firstGuardrailEvent?.data as GuardrailEventData),
    checks: guardrailEvents.flatMap((event) => (event.data as GuardrailEventData).checks || []),
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {message.events.map((event) => {
        const onSelect = () => onEventSelect(event.id);

        if (isGuardrailEvent(event)) {
          if (event.id !== firstGuardrailEvent?.id) {
            return null;
          }
          return (
            <GuardrailChecksEvent
              key={event.id}
              guardrailData={guardrailData}
              isSelected={
                isCurrentMessageSelected && selectedMessageEvent?.data.event_type === AgentMessageEventType.Guardrail
              }
              size={GuardrailChecksEventSize.SMALL}
              onSelect={onSelect}
            />
          );
        }
        if (event.data.event_type === AgentMessageEventType.ToolCall) {
          return (
            <ToolCallEvent
              key={event.id}
              isSelected={isCurrentMessageSelected && selectedMessageEvent?.id === event.id}
              size={ToolCallEventSize.SMALL}
              toolCallData={event.data}
              onSelect={onSelect}
            />
          );
        }
        if (event.data.event_type === AgentMessageEventType.LlmCall) {
          return null;
        }
        if (event.data.event_type === AgentMessageEventType.AgentThought) {
          return (
            <IbCollapse
              key={event.id}
              className={REFLECTIONS_CLASS_NAME}
              expandIcon={({ isActive }) => <IbIcon iconName={isActive ? 'down' : 'right'} size={16} />}
              expandIconPosition="right"
            >
              <IbCollapsePanel
                key="reflections"
                header={
                  <>
                    <IbIcon iconName="thinking-problem" size={16} />
                    <IbTypography.Paragraph type="secondary">
                      {t('Agent: Testing chat: Reflections')}
                    </IbTypography.Paragraph>
                  </>
                }
              >
                <IbTypography.Paragraph italic light type="secondary">
                  {printAgentReflections(event.data)}
                </IbTypography.Paragraph>
              </IbCollapsePanel>
            </IbCollapse>
          );
        }
        if (event.data.event_type === AgentMessageEventType.AssistantMessage) {
          return (
            <div key={event.id} className={ASSISTANT_MESSAGE_CLASS_NAME}>
              <MarkdownContent>{event.data.content}</MarkdownContent>
            </div>
          );
        }
        if (event.data.event_type === AgentMessageEventType.RuntimeError) {
          return (
            <RuntimeErrorEvent
              key={event.id}
              isSelected={isCurrentMessageSelected && selectedMessageEvent?.id === event.id}
              size={RuntimeErrorEventSize.SMALL}
              onSelect={onSelect}
            />
          );
        }
        if (event.data.event_type === AgentMessageEventType.Handoff) {
          return (
            <HandoffEvent
              key={event.id}
              handoffData={event.data}
              isSelected={isCurrentMessageSelected && selectedMessageEvent?.id === event.id}
              size={HandoffEventSize.SMALL}
              onSelect={onSelect}
            />
          );
        }
        if (event.data.event_type === AgentMessageEventType.ChatCompression) {
          return (
            <ChatCompressionEvent
              key={event.id}
              chatCompressionData={event.data}
              isSelected={isCurrentMessageSelected && selectedMessageEvent?.id === event.id}
              size={ChatCompressionEventSize.SMALL}
              onSelect={onSelect}
            />
          );
        }
        throw new Error(`Agent message event type '${event.type}' is not supported`);
      })}
    </div>
  );
};

export default MessageEvents;
